import { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import type { SelectedFile } from '@/components/form/uncontrolled/DragAndDropFileZone/types';
import type { DocumentEntityVersion } from '@/domains/documents/types/documents';
import { EdsDefinitionSections } from '@/features/eds/enums/EdsDefinitionSections';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import { FileContentType } from '@/enums/FileContentType';
import HttpService from '@/services/HttpService/HttpService';
import RecordService from '@/services/RecordService/RecordService';

type IframeGenerationConfig = {
  file?: {
    name: string;
    data: Blob;
    fileType?: FileContentType;
  };
  url?: string;
};

export class DocumentService {
  // public static convertFileToBlob() {} // TODO Documents. Implement after understanding the mechanism and the contract

  // public static uploadFile() {} // TODO Documents. Implement after understanding the mechanism and the contract

  public static async downloadFile(documentTypeName: string, url: string) {
    const response = await HttpService.get<Blob>(
      url,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
        responseType: 'blob',
      },
    );
    const srcFileName = response.headers['content-disposition']
      ?.split(';')
      .map((parameter) => parameter.split('='))
      .filter(([key, _]) => key.trim() === 'filename')?.[0]?.[1];
    const fileName = srcFileName ? `${documentTypeName} - ${srcFileName.replace(/[^\w-_:.\s]/g, '')}` : '';
    const downloadLinkSrc = URL.createObjectURL(response.data);

    const link = document.createElement('a');
    link.href = downloadLinkSrc;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link?.parentNode && link.parentNode.removeChild(link);

    URL.revokeObjectURL(downloadLinkSrc);
  }

  private static buildIframeContent(url: string, fileName?: string) {
    return `
      <!DOCTYPE html>
      <html lang="en">
      <head>
          <meta charset="UTF-8">
          <title>${fileName || 'File preview'}</title>
          <style>
            html, body {
                height: 100%;
                margin: 0;
                overflow: hidden;
            }
            iframe {
                width: 100%;
                height: 100%;
                border: none;
            }
          </style>
      </head>
      <body>
          <iframe id="pdfFrame" src="${url}"></iframe>
      </body>
    </html>
    `;
  }

  public static async mergeLastInstanceVersionIntoDocumentFields(
    normalizedItem: NormalizedV2Response,
    documentInstances: any[],
    section: EdsDefinitionSections,
  ) {
    const lastInstance = (documentInstances || []).sort((documentInstance1, documentInstance2) =>
      documentInstance2.createdAt > documentInstance1.createdAt
        ? 1
        : documentInstance2.createdAt < documentInstance1.createdAt
          ? -1
          : 0,
    )[0];
    const versions: DocumentEntityVersion[] = lastInstance?.documentVersions;
    let extraVersionFields = {};
    let extraVersionEntities = {};

    let extraInstanceFields = {};
    let extraInstanceEntities = {};
    if (versions?.length) {
      const latestVersion = DocumentService.getLatestDocumentVersion(versions);
      const transformedLastVersion = await EdsAdapterService.normalize<NormalizedV2Response>(latestVersion, {
        section,
      });

      const transformedLastInstance = await EdsAdapterService.normalize<NormalizedV2Response>(lastInstance, {
        section,
      });
      extraInstanceFields = transformedLastInstance.fields;
      extraInstanceEntities = {
        ...transformedLastInstance.entities,
        LatestDocumentInstance: { ...transformedLastInstance, fields: {}, entities: {} },
      };

      extraVersionFields = transformedLastVersion.fields;
      extraVersionEntities = {
        ...transformedLastVersion.entities,
        LatestDocumentVersion: { ...transformedLastVersion, fields: {}, entities: {} },
      };
    }
    return {
      ...normalizedItem,
      entities: {
        ...RecordService.omit([EdsEntityCodes.DocumentVersions], extraInstanceEntities),
        ...extraVersionEntities,
        ...RecordService.omit([EdsEntityCodes.DocumentInstances], normalizedItem.entities),
      },
      fields: { ...extraInstanceFields, ...extraVersionFields, ...normalizedItem?.fields },
    };
  }

  public static generatePreviewIframeUrl(config: IframeGenerationConfig) {
    if (config.file) {
      const { name, data, fileType = FileContentType.PDF } = config.file;
      const url = window.URL.createObjectURL(new Blob([data], { type: fileType }));
      return window.URL.createObjectURL(
        new Blob([DocumentService.buildIframeContent(url, name)], { type: 'text/html' }),
      );
    }
    return config.url;
  }

  public static getLatestDocumentVersion<T extends DocumentEntityVersion | NormalizedV2Response>(
    documentEntityVersions: T[],
  ): T {
    return documentEntityVersions.sort((version1, version2) => {
      const normalizedResponseVersionRight = (version2 as NormalizedV2Response).fields?.versionNumber?.value;
      const normalizedResponseVersionLeft = (version1 as NormalizedV2Response).fields?.versionNumber?.value;
      return (
        ((version2.versionNumber as number) ?? normalizedResponseVersionRight) -
        ((version1.versionNumber as number) ?? normalizedResponseVersionLeft)
      );
    })[0];
  }

  public static async getDocumentForm(
    documentTypeId?: number,
    documentFile?: SelectedFile | null,
    documentReference?: boolean,
    createNewInstance?: string,
    preferredExistingDocumentEntityId?: string,
  ) {
    const form = new FormData();
    if (!documentTypeId || !documentFile || [null, undefined].includes(documentReference as any)) {
      return form;
    }
    const arrayBuffer = await documentFile?.arrayBuffer();
    const blob = new Blob([arrayBuffer], { type: documentFile?.type });
    form.append('file', blob, documentFile?.name);
    form.append('documentTypeId', documentTypeId.toString() || '');
    form.append('documentReference', documentReference!.toString() || '');

    if (createNewInstance) {
      form.append('createNewInstance', createNewInstance);
    }

    if (preferredExistingDocumentEntityId != null) {
      form.append('preferredExistingDocumentEntityId', preferredExistingDocumentEntityId.toString());
    }

    return form;
  }
}
